import { 
  ArrowPathIcon, 
  ArrowTrendingUpIcon, 
  ClockIcon,
  ExclamationTriangleIcon,
  UserGroupIcon } from '@heroicons/react/24/outline';
import { BarChart, LineChart, PieChart } from '@mui/x-charts';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';

import { fetchPerformance, setReloadPerf } from '../../../store/CoproReducer';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import { ROUTES } from '../../../utils/routes';
import KPICard from '../../common/KPICards';
import MUITable from '../../common/MUITable';

// CRM Flow Steps
const crmSteps = {
  step1: {
    title: 'Etape 1: Qualification des DPEs',
    categories: [
      { id: 'aTraiter', name: 'À traiter' },
      { id: 'sansProprietaire', name: 'Sans propriétaire' },
      { id: 'nonTraite', name: 'Non traité' },
      { id: 'nonTraiteSansProprietaire', name: 'Non traité (sans propriétaire)' },
    ],
  },
  step2: {
    title: 'Etape 2: Communication avec le propriétaire',
    categories: [
      { id: 'nonContacte', name: 'Non contacté' },
      { id: 'enAttenteRetour', name: 'En attente de retour' },
      { id: 'aAppeler', name: 'À appeler' },
      { id: 'demandesRappel', name: 'Demandes de rappels' },
    ],
  },
  step3Transaction: {
    title: 'Etape 3: Transaction',
    categories: [
      { id: 'aContacterTransaction', name: 'À contacter' },
      { id: 'enAttenteRdvTransaction', name: 'En attente de rdv' },
      { id: 'refusTransaction', name: 'Refus' },
      { id: 'signeTransaction', name: 'Signé' },
    ],
  },
  step3Gestion: {
    title: 'Etape 3: Gestion Locative',
    categories: [
      { id: 'aContacterGestion', name: 'À contacter' },
      { id: 'enAttenteRdvGestion', name: 'En attente de rdv' },
      { id: 'refusGestion', name: 'Refus' },
      { id: 'signeGestion', name: 'Signé' },
    ],
  },
};

// Mock data structure - replace with actual API data
const mockPerformanceData = {
  summary: {
    totalDPE: 548,
    averageTimeStep1: 3.2, // days
    averageTimeStep2: 5.1, // days
    averageTimeStep3Transaction: 12.3, // days
    averageTimeStep3Gestion: 10.8, // days
    totalSigned: 58,
    conversionRate: 10.6, // percentage
  },
  data: {
    // Step 1
    aTraiter: { count: 124, avgTime: 2.1 },
    sansProprietaire: { count: 68, avgTime: 3.4 },
    nonTraite: { count: 42, avgTime: 'N/A' },
    nonTraiteSansProprietaire: { count: 35, avgTime: 'N/A' },
    
    // Step 2
    nonContacte: { count: 82, avgTime: 4.5 },
    enAttenteRetour: { count: 63, avgTime: 6.2 },
    aAppeler: { count: 41, avgTime: 3.8 },
    demandesRappel: { count: 29, avgTime: 5.7 },
    
    // Step 3 Transaction
    aContacterTransaction: { count: 25, avgTime: 10.4 },
    enAttenteRdvTransaction: { count: 18, avgTime: 14.2 },
    refusTransaction: { count: 15, avgTime: 8.5 },
    signeTransaction: { count: 32, avgTime: 16.1 },
    
    // Step 3 Gestion
    aContacterGestion: { count: 22, avgTime: 9.2 },
    enAttenteRdvGestion: { count: 14, avgTime: 12.5 },
    refusGestion: { count: 11, avgTime: 7.8 },
    signeGestion: { count: 26, avgTime: 13.9 },
  },
  // Last 6 months of data for trends
  trends: [
    { month: 'Janvier', totalDPE: 82, step1: 45, step2: 20, step3: 17 },
    { month: 'Février', totalDPE: 95, step1: 52, step2: 25, step3: 18 },
    { month: 'Mars', totalDPE: 88, step1: 48, step2: 22, step3: 18 },
    { month: 'Avril', totalDPE: 105, step1: 58, step2: 28, step3: 19 },
    { month: 'Mai', totalDPE: 92, step1: 50, step2: 24, step3: 18 },
    { month: 'Juin', totalDPE: 86, step1: 47, step2: 23, step3: 16 },
  ],
};

function Performance() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState(mockPerformanceData);
  const [months, setMonths] = useState(6);
  
  const company = useAppSelector((state) => state.auth.company);
  const reloadPerf = useAppSelector((state) => state.copro.reloadPerf);

  useEffect(() => {
    if (company || reloadPerf) {
      dispatch(fetchPerformance())
        .then(() => {
          setLoading(false);
          dispatch(setReloadPerf(false));
          // In reality, you would process the real data here instead of using mock data
          setPerformanceData(mockPerformanceData);
        });
    }
  }, [company, reloadPerf, dispatch]);

  const getStepTotalCount = (stepKey) => {
    return crmSteps[stepKey].categories.reduce((total, category) => {
      return total + (performanceData.data[category.id]?.count || 0);
    }, 0);
  };

  const getStepTotalTime = (stepKey) => {
    let totalTime = 0;
    let count = 0;
    
    crmSteps[stepKey].categories.forEach(category => {
      if (performanceData.data[category.id] && typeof performanceData.data[category.id].avgTime === 'number') {
        totalTime += performanceData.data[category.id].avgTime * performanceData.data[category.id].count;
        count += performanceData.data[category.id].count;
      }
    });
    
    return count > 0 ? (totalTime / count).toFixed(1) : 0;
  };

  // Header for the step tables
  const stepTableHeaders = [
    { title: 'Catégorie', class: 'w-1/3' },
    { title: 'Nombre', class: 'w-1/3' },
    { title: 'Temps moyen (jours)', class: 'w-1/3' },
  ];

  // Function to create table data for each step
  const createStepTableData = (stepKey) => {
    return crmSteps[stepKey].categories.map(category => ({
      'Catégorie': category.name,
      'Nombre': (
        <span className="flex justify-center">
          {performanceData.data[category.id]?.count || 0}
          &nbsp;
          <div
            className="cursor-pointer text-rgpd-blue-light hover:underline"
            onClick={() => navigate(ROUTES.PROSPECTING_COMMUNICATE, { state: { filterCategory: category.id } })}
          >
            (Voir le détail)
          </div>
        </span>
      ),
      'Temps moyen (jours)': typeof performanceData.data[category.id]?.avgTime === 'number' 
        ? performanceData.data[category.id]?.avgTime.toFixed(1) 
        : performanceData.data[category.id]?.avgTime || '-',
    }));
  };

  const refreshData = () => {
    setLoading(true);
    dispatch(setReloadPerf(true));
  };

  return (
    <div>
      {loading ? (
        <div className="mb-8 rounded-lg bg-white p-4 shadow-md">
          <Skeleton count={10} height={45} />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Development Warning Banner */}
          <div className="rounded-r-lg border-l-4 border-amber-500 bg-amber-50 p-4 shadow-md">
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-amber-500" />
              <div>
                <h3 className="font-semibold text-amber-800">Page en cours de développement</h3>
                <p className="text-sm text-amber-700">
                  Cette page est actuellement en cours de développement. Les données affichées sont des données de démonstration et ne doivent pas être prises en compte.
                </p>
              </div>
            </div>
          </div>

          {/* Dashboard header with summary KPIs */}
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium text-rgpd-white">Période de données : </span>
              <select 
                className="rounded-md border border-rgpd-blue px-3 py-2 text-rgpd-blue focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
                onChange={(e) => {
                  // Handle period change
                  const selectedMonths = parseInt(e.target.value, 10);
                  setMonths(selectedMonths);
                  setLoading(true);
                  // In a real implementation, you would add a parameter to the fetchPerformance call
                  dispatch(fetchPerformance())
                    .then(() => {
                      setLoading(false);
                      console.warn('months changed to ', selectedMonths);
                      // You would process the real data with the selected period here
                      setPerformanceData(mockPerformanceData);
                    });
                }}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                  <option key={month} value={month} selected={month === months}>
                    {month} {month === 1 ? 'mois' : 'mois'}
                  </option>
                ))}
              </select>
            </div>
            <button 
              onClick={refreshData}
              className="flex items-center gap-2 rounded-lg bg-rgpd-white px-4 py-2 text-rgpd-blue transition-colors hover:bg-rgpd-blue-light hover:text-white"
            >
              <ArrowPathIcon className="h-5 w-5" />
              Actualiser
            </button>
          </div>

          {/* Summary KPIs */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <KPICard
              icon={<UserGroupIcon className="h-10 w-10" />}
              title="Total DPEs"
              value={performanceData.summary.totalDPE}
              subtitles={[
                { label: 'Conversion', value: `${performanceData.summary.conversionRate}%` },
              ]}
            />
            <KPICard
              icon={<ArrowTrendingUpIcon className="h-10 w-10" />}
              title="Total Signés"
              value={performanceData.summary.totalSigned}
              subtitles={[
                { label: 'Transaction', value: performanceData.data.signeTransaction.count },
                { label: 'Gestion', value: performanceData.data.signeGestion.count },
              ]}
              color="text-rgpd-green"
            />
            <KPICard
              icon={<ClockIcon className="h-10 w-10" />}
              title="Temps moyen traitement"
              value={`${(
                performanceData.summary.averageTimeStep1 + 
                performanceData.summary.averageTimeStep2 + 
                ((performanceData.summary.averageTimeStep3Transaction + performanceData.summary.averageTimeStep3Gestion) / 2)
              ).toFixed(1)} jours`}
              subtitles={[
                { label: 'Etape 1', value: `${performanceData.summary.averageTimeStep1} jours` },
                { label: 'Etape 2', value: `${performanceData.summary.averageTimeStep2} jours` },
              ]}
              color="text-rgpd-blue-light"
            />
            <KPICard
              icon={<UserGroupIcon className="h-10 w-10" />}
              title="Non traités"
              value={performanceData.data.nonTraite.count + performanceData.data.nonTraiteSansProprietaire.count}
              subtitles={[
                { label: 'Avec propriétaire', value: performanceData.data.nonTraite.count },
                { label: 'Sans propriétaire', value: performanceData.data.nonTraiteSansProprietaire.count },
              ]}
              color="text-orange-500"
            />
          </div>

          {/* Funnel visualization */}
          <div className="rounded-lg bg-white p-6 shadow-md">
            <h2 className="mb-4 text-xl font-semibold">Entonnoir de Conversion</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <h3 className="mb-3 text-lg font-medium">Répartition par étape</h3>
                <PieChart
                  series={[
                    {
                      data: [
                        { id: 0, value: getStepTotalCount('step1'), label: 'Etape 1: Qualification des DPEs' },
                        { id: 1, value: getStepTotalCount('step2'), label: 'Etape 2: Communication avec le propriétaire' },
                        { id: 2, value: getStepTotalCount('step3Transaction'), label: 'Etape 3: Transaction' },
                        { id: 3, value: getStepTotalCount('step3Gestion'), label: 'Etape 3: Gestion Locative' },
                      ],
                      highlightScope: { faded: 'global', highlighted: 'item' },
                      faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                    },
                  ]}
                  height={400}
                  margin={{ top: 10, bottom: 150, left: 30, right: 30 }}
                  slotProps={{
                    legend: {
                      direction: 'column',
                      position: { vertical: 'bottom', horizontal: 'middle' },
                      padding: 20,
                    },
                  }}
                />
              </div>
              <div>
                <h3 className="mb-3 text-lg font-medium">Tendance sur 6 mois</h3>
                <LineChart
                  xAxis={[{ 
                    data: performanceData.trends.map(t => t.month),
                    scaleType: 'band',
                  }]}
                  series={[
                    {
                      data: performanceData.trends.map(t => t.step1),
                      label: 'Etape 1 : Qualification des DPEs',
                      area: true,
                      color: 'rgb(99, 102, 241)',
                    },
                    {
                      data: performanceData.trends.map(t => t.step2),
                      label: 'Etape 2 : Communication avec le propriétaire',
                      area: true,
                      color: 'rgb(14, 165, 233)',
                    },
                    {
                      data: performanceData.trends.map(t => t.step3),
                      label: 'Etape 3 : Transaction et Gestion Locative',
                      area: true,
                      color: 'rgb(34, 211, 238)',
                    },
                  ]}
                  height={300}
                  margin={{ top: 10, bottom: 150, left: 30, right: 30 }}
                  slotProps={{
                    legend: {
                      direction: 'column',
                      position: { vertical: 'bottom', horizontal: 'middle' },
                      padding: 20,
                    },
                  }}
                />
              </div>
            </div>
          </div>

          {/* Step 1 Section */}
          <div className="rounded-lg bg-white p-6 shadow-md">
            <h2 className="mb-4 text-xl font-semibold">{crmSteps.step1.title}</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <MUITable 
                  data={createStepTableData('step1')}
                  headers={stepTableHeaders}
                  tableClass="text-center"
                  disablePagination
                />
              </div>
              <div>
                <h3 className="mb-3 text-lg font-medium">Statistiques</h3>
                <div className="grid grid-cols-1 gap-4">
                  <KPICard
                    icon={<UserGroupIcon className="h-8 w-8" />}
                    title="Total des dossiers en cours dans cette étape"
                    value={getStepTotalCount('step1')}
                  />
                  <KPICard
                    icon={<ClockIcon className="h-8 w-8" />}
                    title="Temps moyen de traitement par catégorie de l'étape"
                    value={`${getStepTotalTime('step1')} jours`}
                    color="text-rgpd-blue-light"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 Section */}
          <div className="rounded-lg bg-white p-6 shadow-md">
            <h2 className="mb-4 text-xl font-semibold">{crmSteps.step2.title}</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <MUITable 
                  data={createStepTableData('step2')}
                  headers={stepTableHeaders}
                  tableClass="text-center"
                  disablePagination
                />
              </div>
              <div>
                <h3 className="mb-3 text-lg font-medium">Statistiques</h3>
                <div className="grid grid-cols-1 gap-4">
                  <KPICard
                    icon={<UserGroupIcon className="h-8 w-8" />}
                    title="Total des dossiers en cours dans cette étape"
                    value={getStepTotalCount('step2')}
                  />
                  <KPICard
                    icon={<ClockIcon className="h-8 w-8" />}
                    title="Temps moyen de traitement par catégorie de l'étape"
                    value={`${getStepTotalTime('step2')} jours`}
                    color="text-rgpd-blue-light"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 Transaction Section */}
          <div className="rounded-lg bg-white p-6 shadow-md">
            <h2 className="mb-4 text-xl font-semibold">{crmSteps.step3Transaction.title}</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <MUITable 
                  data={createStepTableData('step3Transaction')}
                  headers={stepTableHeaders}
                  tableClass="text-center"
                  disablePagination
                />
              </div>
              <div>
                <h3 className="mb-3 text-lg font-medium">Statistiques</h3>
                <div className="grid grid-cols-1 gap-4">
                  <KPICard
                    icon={<UserGroupIcon className="h-8 w-8" />}
                    title="Total des dossiers en cours dans cette étape"
                    value={getStepTotalCount('step3Transaction')}
                  />
                  <KPICard
                    icon={<ClockIcon className="h-8 w-8" />}
                    title="Temps moyen de traitement par catégorie de l'étape"
                    value={`${getStepTotalTime('step3Transaction')} jours`}
                    color="text-rgpd-blue-light"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 Gestion Section */}
          <div className="rounded-lg bg-white p-6 shadow-md">
            <h2 className="mb-4 text-xl font-semibold">{crmSteps.step3Gestion.title}</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <MUITable 
                  data={createStepTableData('step3Gestion')}
                  headers={stepTableHeaders}
                  tableClass="text-center"
                  disablePagination
                />
              </div>
              <div>
                <h3 className="mb-3 text-lg font-medium">Statistiques</h3>
                <div className="grid grid-cols-1 gap-4">
                  <KPICard
                    icon={<UserGroupIcon className="h-8 w-8" />}
                    title="Total des dossiers en cours dans cette étape"
                    value={getStepTotalCount('step3Gestion')}
                  />
                  <KPICard
                    icon={<ClockIcon className="h-8 w-8" />}
                    title="Temps moyen de traitement par catégorie de l'étape"
                    value={`${getStepTotalTime('step3Gestion')} jours`}
                    color="text-rgpd-blue-light"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Comparative Performance */}
          <div className="rounded-lg bg-white p-6 shadow-md">
            <h2 className="mb-4 text-xl font-semibold">Comparaison par Phase</h2>
            <BarChart
              xAxis={[
                { 
                  scaleType: 'band', 
                  data: ['Temps moyen de traitement (jours)'], 
                },
              ]}
              series={[
                {
                  data: [performanceData.summary.averageTimeStep1],
                  label: 'Etape 1: Qualification des DPEs',
                  color: 'rgb(99, 102, 241)',
                },
                {
                  data: [performanceData.summary.averageTimeStep2],
                  label: 'Etape 2: Communication avec le propriétaire',
                  color: 'rgb(14, 165, 233)',
                },
                {
                  data: [performanceData.summary.averageTimeStep3Transaction],
                  label: 'Etape 3: Transaction',
                  color: 'rgb(34, 211, 238)',
                },
                {
                  data: [performanceData.summary.averageTimeStep3Gestion],
                  label: 'Etape 3: Gestion Locative',
                  color: 'rgb(125, 211, 252)',
                },
              ]}
              height={300}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Performance;
